<script setup>
import ApikomTaskTable from "@frontJs/components/tables/projectManagement/tasks/ApikomTaskTable.vue";
import ProjectManagementLayout from "@frontJs/layouts/ProjectManagementLayout.vue";
import { Head } from "@inertiajs/vue3";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import PageTitle from "@js/components/titles/PageTitle.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { useRoute } from "@js/utils/route";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { route } = useRoute();

const loading = ref(true);

const selectedTab = ref(0);

const allCount = ref([]);

function changeTab(index) {
  selectedTab.value = index;
}

function updateData() {
  loading.value = true;

  const statutes = [
    ["open", "to_process"],
    ["open", "to_process"],
    ["open", "to_process"],
    ["open", "to_process"],
    ["open", "to_process"],
    null,
  ];

  const types = [
    ["hourly", "package"],
    ["hourly", "package"],
    ["project"],
    ["project"],
    ["project"],
    null,
  ];

  const themes = [
    null,
    null,
    ["dev"],
    ["dev"],
    ["seo", "sea", "smo", "sma", "sem", "cs"],
    null,
  ];

  const noOneResponsible = [null, true, false, true, null, null];

  const idea = [false, false, false, false, false, true];

  const promises = statutes.map((status, index) => {
    return new Promise((resolve, reject) => {
      let url = route("front.projectManagement.task.postTaskCount");

      let postData = {
        types: types[index],
        statutes: status,
        noOneResponsible: noOneResponsible[index],
        idea: idea[index],
        themes: themes[index],
      };

      let config = {};

      axios
        .post(url, postData, config)
        .then((response) => {
          resolve(response.data.count);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  });

  Promise.all(promises)
    .then((counts) => {
      allCount.value = counts;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

onMounted(() => {
  updateData();
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
  <Head>
    <title>
      {{ t("title.apikomTasks") }} - {{ t("title.tasks") }} -
      {{ t("title.projectManagement") }} - {{ t("title.datakepler") }}
    </title>
  </Head>

  <PageTitle>
    {{ t("title.projectManagement") }} - {{ t("title.tasks") }} -
    {{ t("title.apikomTasks") }}
  </PageTitle>

  <LoadingSpinner v-if="loading" />

  <TabGroup
    v-else
    :selected-tab="selectedTab"
    :change-tab="changeTab"
    :tabs="{
      tasks: t('title.tasks') + ' (' + allCount[0] + ')',
      devToTake: t('title.devToTake') + ' (' + allCount[1] + ')',
      devProjects: t('title.devProjects') + ' (' + allCount[2] + ')',
      devProjectsToTake:
        t('title.devProjectsToTake') + ' (' + allCount[3] + ')',
      otherProjects: t('title.otherProjects') + ' (' + allCount[4] + ')',
      closedTasks: t('title.closedTasks'),
      closedProjects: t('title.closedProjects'),
      idea: t('title.ideas') + ' (' + allCount[5] + ')',
    }"
  >
    <template #panels>
      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks"
          :type="['hourly', 'package']"
          :status="['open', 'to_process']"
          :create-task-url="
            route('front.projectManagement.task.createTask')
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_dev_to_take"
          :type="['hourly', 'package']"
          :status="['open', 'to_process']"
          no-one-responsible
          :create-task-url="
            route('front.projectManagement.task.createTask', {
              theme: 'dev',
              noOneResponsible: true,
            })
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_dev_project"
          type="project"
          :theme="['dev', 'dev_seo']"
          :status="['open', 'to_process']"
          :no-one-responsible="false"
          :create-task-url="
            route('front.projectManagement.task.createTask', {
              type: 'project',
              theme: 'dev',
            })
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_dev_projects_to_take"
          :type="['project']"
          :theme="['dev', 'dev_seo']"
          :status="['open', 'to_process']"
          no-one-responsible
          :create-task-url="
            route('front.projectManagement.task.createTask', {
              type: 'project',
              theme: 'dev',
              noOneResponsible: true,
            })
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_other_projects"
          :type="['project']"
          :theme="['seo', 'sea', 'smo', 'sma', 'sem', 'cs']"
          :status="['open', 'to_process']"
          :create-task-url="
            route('front.projectManagement.task.createTask', {
              type: 'project',
            })
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_closed"
          :type="['hourly', 'package']"
          status="closed"
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_closed_project"
          type="project"
          status="closed"
          :create-task-url="
            route('front.projectManagement.task.createTask', {
              type: 'project',
            })
          "
        />
      </TabPanel>

      <TabPanel without-background :unmount="true">
        <ApikomTaskTable
          user-session-key="tasks_idea"
          :create-task-url="
            route('front.projectManagement.task.createTaskIdea')
          "
          idea
        />
      </TabPanel>
    </template>
  </TabGroup>
</template>
